import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  PlayCircleIcon,
  EnvelopeIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import billingApi, { BillingSystemStatus as BillingSystemStatusData } from '../../services/api/billing.api';

interface Props {
  isAdmin: boolean;
}

export default function BillingSystemStatus({ isAdmin }: Props) {
  const { t, i18n } = useTranslation('billing');
  const [data, setData] = useState<BillingSystemStatusData | null>(null);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState<'invoice' | 'dunning' | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await billingApi.getSystemStatus();
      if (res.success) setData(res.data);
    } catch {
      // admin-only — silent for non-admins
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) load();
  }, [isAdmin]);

  if (!isAdmin) return null;

  const fmtDate = (s: string | null) => {
    if (!s) return '—';
    try {
      return new Date(s).toLocaleString(i18n.language || 'en-US');
    } catch {
      return s;
    }
  };

  const fmtMoney = (n: number, cur = 'USD') =>
    new Intl.NumberFormat(cur === 'BRL' ? 'pt-BR' : 'en-US', {
      style: 'currency',
      currency: cur,
    }).format(Number(n) || 0);

  const handleRunInvoices = async () => {
    setRunning('invoice');
    try {
      const res = await billingApi.runInvoiceGeneration();
      if (res.success) {
        toast.success(
          t('systemStatus.invoiceRunOk', {
            generated: res.data.generated,
            skipped: res.data.skipped,
            failed: res.data.failed,
            defaultValue: 'Generated {{generated}} invoices ({{skipped}} skipped, {{failed}} failed)',
          })
        );
        await load();
      }
    } catch (err: any) {
      toast.error(err?.message || 'Failed');
    } finally {
      setRunning(null);
    }
  };

  const handleRunDunning = async () => {
    setRunning('dunning');
    try {
      const res = await billingApi.runDunning();
      if (res.success) {
        toast.success(
          t('systemStatus.dunningRunOk', {
            retried: res.data.retried,
            failed: res.data.failed,
            suspended: res.data.suspended,
            defaultValue:
              'Retried {{retried}}, {{failed}} failed, {{suspended}} suspended',
          })
        );
        await load();
      }
    } catch (err: any) {
      toast.error(err?.message || 'Failed');
    } finally {
      setRunning(null);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <ClockIcon className="h-6 w-6 text-indigo-600 dark:text-indigo-400" />
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
            {t('systemStatus.title', 'Billing System Status')}
          </h3>
        </div>
        <button
          onClick={load}
          className="text-sm text-indigo-600 dark:text-indigo-400 hover:underline flex items-center gap-1"
        >
          <ArrowPathIcon className="h-4 w-4" />
          {t('systemStatus.refresh', 'Refresh')}
        </button>
      </div>

      {loading && !data && (
        <p className="text-gray-500 dark:text-gray-400 text-sm">
          {t('systemStatus.loading', 'Loading…')}
        </p>
      )}

      {data && (
        <div className="space-y-6">
          {/* Cron jobs */}
          <div>
            <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              {t('systemStatus.cronJobs', 'Scheduled jobs')}
            </h4>
            {data.cron ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {data.cron.jobs.map((job) => (
                  <div
                    key={job.name}
                    className="flex items-center justify-between gap-2 px-3 py-2 rounded border border-gray-200 dark:border-gray-700"
                  >
                    <div className="flex items-center gap-2">
                      {job.running ? (
                        <CheckCircleIcon className="h-4 w-4 text-green-600" />
                      ) : (
                        <ExclamationTriangleIcon className="h-4 w-4 text-yellow-500" />
                      )}
                      <span className="text-sm text-gray-900 dark:text-white">{job.name}</span>
                    </div>
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {t('systemStatus.next', 'next')}: {fmtDate(job.nextRun)}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-red-600">
                {t('systemStatus.cronUnavailable', 'Cron service is not running')}
              </p>
            )}
          </div>

          {/* Counters */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Counter label={t('systemStatus.emailPending', 'Email pending')} value={data.emailQueue?.pending} />
            <Counter label={t('systemStatus.emailSent', 'Email sent (30d)')} value={data.emailQueue?.sent} />
            <Counter label={t('systemStatus.emailFailed', 'Email failed')} value={data.emailQueue?.failed} tone="warn" />
            <Counter label={t('systemStatus.emailDead', 'Email exhausted')} value={data.emailQueue?.dead} tone="bad" />
            <Counter label={t('systemStatus.invoicesPending', 'Invoices pending')} value={data.invoices?.pending} />
            <Counter label={t('systemStatus.invoicesOverdue', 'Invoices overdue')} value={data.invoices?.overdue} tone="warn" />
            <Counter label={t('systemStatus.invoicesUncollectible', 'Uncollectible')} value={data.invoices?.uncollectible} tone="bad" />
            <Counter label={t('systemStatus.invoicesMissingPdf', 'Missing PDF')} value={data.invoices?.missing_pdf} tone="warn" />
          </div>

          {/* Dunning queue */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                {t('systemStatus.dunningQueue', 'Payment retry queue')}
              </h4>
            </div>
            {data.dunningQueue?.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t('systemStatus.dunningEmpty', 'No invoices in retry queue.')}
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-gray-500 dark:text-gray-400">
                      <th className="py-2">{t('systemStatus.invoice', 'Invoice')}</th>
                      <th>{t('systemStatus.status', 'Status')}</th>
                      <th>{t('systemStatus.attempts', 'Attempts')}</th>
                      <th>{t('systemStatus.nextRetry', 'Next retry')}</th>
                      <th>{t('systemStatus.amountDue', 'Amount due')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.dunningQueue.map((row) => (
                      <tr key={row.id} className="border-t border-gray-200 dark:border-gray-700">
                        <td className="py-2 font-mono text-xs">{row.invoice_number}</td>
                        <td className="text-gray-700 dark:text-gray-300">{row.status}</td>
                        <td>{row.retry_attempts}</td>
                        <td className="text-xs text-gray-600 dark:text-gray-400">
                          {fmtDate(row.next_retry_at)}
                        </td>
                        <td>{fmtMoney(row.amount_due, row.currency)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex flex-wrap gap-2">
            <button
              onClick={handleRunInvoices}
              disabled={running === 'invoice'}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
            >
              <PlayCircleIcon className="h-5 w-5" />
              {running === 'invoice'
                ? t('systemStatus.running', 'Running…')
                : t('systemStatus.runInvoices', 'Run invoice generation now')}
            </button>
            <button
              onClick={handleRunDunning}
              disabled={running === 'dunning'}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 disabled:opacity-50"
            >
              <EnvelopeIcon className="h-5 w-5" />
              {running === 'dunning'
                ? t('systemStatus.running', 'Running…')
                : t('systemStatus.runDunning', 'Run dunning now')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Counter({
  label,
  value,
  tone = 'ok',
}: {
  label: string;
  value: number | null | undefined;
  tone?: 'ok' | 'warn' | 'bad';
}) {
  const v = Number(value || 0);
  const color =
    tone === 'bad'
      ? 'text-red-600 dark:text-red-400'
      : tone === 'warn'
      ? 'text-yellow-600 dark:text-yellow-400'
      : 'text-gray-900 dark:text-white';
  return (
    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
      <p className="text-xs text-gray-600 dark:text-gray-400">{label}</p>
      <p className={`text-2xl font-semibold ${color}`}>{v}</p>
    </div>
  );
}
